// src/components/shared/InputWithSlug.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './input';
import { Label } from './label';
import { Button } from './button';
import { cn, generateSlug } from '../../lib/utils'; // Assuming generateSlug is also in utils.ts
import { RefreshCcw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface InputWithSlugProps {
  titleLabel?: string;
  slugLabel?: string;
  titlePlaceholder?: string;
  slugPlaceholder?: string;
  initialTitle?: string;
  initialSlug?: string;
  onTitleChange?: (title: string) => void;
  onSlugChange?: (slug: string, isManuallyEdited: boolean) => void;
  className?: string;
  titleInputName?: string;
  slugInputName?: string;
  titleInputId?: string;
  slugInputId?: string;
}

export function InputWithSlug({
  titleLabel = "Title",
  slugLabel = "Slug",
  titlePlaceholder = "Enter title...",
  slugPlaceholder = "Enter slug...",
  initialTitle = "",
  initialSlug = "",
  onTitleChange,
  onSlugChange,
  className,
  titleInputName = "title",
  slugInputName = "slug",
  titleInputId = "title-input-with-slug",
  slugInputId = "slug-input-with-slug",
}: InputWithSlugProps) {
  const [title, setTitle] = useState<string>(initialTitle);
  const [slug, setSlug] = useState<string>(initialSlug);
  // If an initial slug is provided and it's different from what would be generated from initialTitle,
  // assume it was manually set.
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState<boolean>(
    () => !!initialSlug && initialSlug !== generateSlug(initialTitle)
  );

  // Auto-generate slug from title if not manually edited
  useEffect(() => {
    if (!isSlugManuallyEdited) {
      const newSlug = generateSlug(title);
      if (newSlug !== slug) { // Only update if different to avoid loop with onSlugChange
        setSlug(newSlug);
      }
    }
  }, [title, isSlugManuallyEdited, slug]); // added slug to dependency array

  // Callbacks for parent component
  useEffect(() => {
    onTitleChange?.(title);
  }, [title, onTitleChange]);

  useEffect(() => {
    onSlugChange?.(slug, isSlugManuallyEdited);
  }, [slug, isSlugManuallyEdited, onSlugChange]);


  const handleTitleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = event.target.value;
    setTitle(newTitle);
    // If the current slug was derived from the *previous* title,
    // or if the slug input is empty, allow auto-generation to resume for the new title.
    // This logic makes it so if the slug perfectly matches the auto-generated one, it will keep updating.
    // If the user then manually changes the slug, isSlugManuallyEdited will be set to true.
    const currentAutoSlug = generateSlug(newTitle);
    if (slug === generateSlug(title) || slug === "" || currentAutoSlug === slug) {
       setIsSlugManuallyEdited(false);
    }
    // If user clears title, clear slug and allow auto-generation
    if (newTitle === "") {
      setIsSlugManuallyEdited(false);
      setSlug("");
    }
  };

  const handleSlugInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(event.target.value); // Allow any characters during typing
    setIsSlugManuallyEdited(true);
  };

  const handleSlugInputBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Clean up the manually entered slug on blur
    const cleanedSlug = generateSlug(event.target.value);
    setSlug(cleanedSlug);
  };

  const handleResetSlug = useCallback(() => {
    setIsSlugManuallyEdited(false);
    const newSlug = generateSlug(title); // Re-generate from current title
    setSlug(newSlug);
  }, [title]);

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <Label htmlFor={titleInputId} className="mb-1 block">{titleLabel}</Label>
        <Input
          id={titleInputId}
          type="text"
          name={titleInputName}
          placeholder={titlePlaceholder}
          value={title}
          onChange={handleTitleInputChange}
        />
      </div>
      <div>
        <Label htmlFor={slugInputId} className="mb-1 block">{slugLabel}</Label>
        <div className="flex items-center space-x-2">
          <Input
            id={slugInputId}
            type="text"
            name={slugInputName}
            placeholder={slugPlaceholder}
            value={slug}
            onChange={handleSlugInputChange}
            onBlur={handleSlugInputBlur}
            aria-describedby={`${slugInputId}-helper`}
            className="flex-1"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleResetSlug}
                  type="button" // Important: Prevents form submission if inside a form
                  aria-label="Reset slug to auto-generate from title"
                >
                  <RefreshCcw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset slug (auto-generate from title)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p id={`${slugInputId}-helper`} className="text-sm text-muted-foreground mt-1">
          {isSlugManuallyEdited && slug !== ""
            ? "Slug manually edited. "
            : slug === "" && title === ""
            ? "Type a title to auto-generate slug."
            : "Slug auto-generated from title. "}
          You can edit this.
        </p>
      </div>
    </div>
  );
}